import { createSelector } from 'reselect';
import deepmerge from 'deepmerge';

import { api, appointmentDraftSelectors, bookingsSelectors, journeyStateSelectors } from '~/api';
import { getSafety, isNotEmpty } from '~/utils';
import { envConfigSelectors as envSelectors, servicesSelectors } from '~/selectors';
import { selectAnswers } from '~/api/bookings/bookings.selectors';
import { questionsSelectors } from '~/components/questions';

const remote = (version) => (path) => `remote.${version}.${path}`;

export const selectServiceEnvConfigByAppointmentTypeId = createSelector(
  envSelectors.selectServicesEnvConfig,
  appointmentDraftSelectors.selectAppointmentTypeId,
  bookingsSelectors.selectAppointmentTypeId,
  (servicesEnvConfig, appointmentTypeId, bookingAppointmentTypeId) =>
    Object.values(servicesEnvConfig).find((service) =>
      Object.keys(service.appointmentTypes).find(
        (id) => id === (appointmentTypeId || bookingAppointmentTypeId),
      ),
    ) || {},
);

export const selectAttendeeQuestionIdsGroup = createSelector(
  selectServiceEnvConfigByAppointmentTypeId,
  (serviceEnvConfig) => serviceEnvConfig.attendeeQuestionIdsGroup,
);

export const selectAppointmentForSomeoneElseQuestionIds = createSelector(
  selectAttendeeQuestionIdsGroup,
  ({ appointmentForSomeoneElseId = '' } = {}) =>
    appointmentForSomeoneElseId
      .split(':')
      .filter(Boolean)
      .map((q) => `q${q}`),
);

export const selectAttendeeNameQuestionIds = createSelector(
  selectAttendeeQuestionIdsGroup,
  ({ attendeeNameId = '' } = {}) =>
    attendeeNameId
      .split(':')
      .filter(Boolean)
      .map((q) => `q${q}`),
);

export const selectAttendeePhoneQuestionIds = createSelector(
  selectAttendeeQuestionIdsGroup,
  ({ attendeePhoneId = '' } = {}) =>
    attendeePhoneId
      .split(':')
      .filter(Boolean)
      .map((q) => `q${q}`),
);

export const selectGiftAppointmentQuestionIds = createSelector(
  selectAttendeeQuestionIdsGroup,
  ({ giftAppointmentId = '' } = {}) =>
    giftAppointmentId
      .split(':')
      .filter(Boolean)
      .map((q) => `q${q}`),
);

export const selectServiceConfig = (envConfig, appointmentTypeId) =>
  Object.values(envConfig.services).find((service) =>
    Object.keys(service.appointmentTypes).find((id) => id === appointmentTypeId),
  ) || {};

export const selectAppointmentTypeConfig = (envConfig, appointmentTypeId) => {
  const { appointmentTypes } = selectServiceConfig(envConfig, appointmentTypeId);
  return appointmentTypes && appointmentTypes[appointmentTypeId]
    ? appointmentTypes[appointmentTypeId]
    : {};
};

export const selectRemoteV2Config = (envConfig, appointmentTypeId, remoteConfig) => {
  const appointmentTypeConfig = selectAppointmentTypeConfig(envConfig, appointmentTypeId);
  return appointmentTypeConfig && appointmentTypeConfig.remote === 'v2' ? remoteConfig : {};
};

export const selectRemoteV3Config = (envConfig, appointmentTypeId, remoteConfig) => {
  const appointmentTypeConfig = selectAppointmentTypeConfig(envConfig, appointmentTypeId);
  return appointmentTypeConfig && appointmentTypeConfig.remote === 'v3' ? remoteConfig : {};
};

export const selectRemoteV2ConfigProperty = (
  envConfig,
  appointmentTypeId,
  remoteConfig,
  configPropertyName,
) =>
  (isNotEmpty(selectRemoteV2Config(envConfig, appointmentTypeId, remoteConfig)) &&
    remoteConfig &&
    remoteConfig[configPropertyName]) ||
  null;

export const selectRemoteV3ConfigProperty = (
  envConfig,
  appointmentTypeId,
  remoteConfig,
  configPropertyName,
) =>
  (isNotEmpty(selectRemoteV3Config(envConfig, appointmentTypeId, remoteConfig)) &&
    remoteConfig &&
    remoteConfig[configPropertyName]) ||
  null;

export const selectRemoteV1ConfigProperty = (
  envConfig,
  appointmentTypeId,
  remoteConfig,
  configPropertyName,
) => {
  const appointmentTypeConfig = selectAppointmentTypeConfig(envConfig, appointmentTypeId);
  return (
    (appointmentTypeConfig &&
      appointmentTypeConfig.remote === 'v1' &&
      remoteConfig &&
      remoteConfig[configPropertyName]) ||
    null
  );
};

export const selectThisAppointmentIsForSomeoneElseQuestionId = ({ envConfig, bookings }) => {
  const { booking } = bookings;

  const questionIdsGroup =
    booking &&
    booking.appointmentType &&
    selectServiceConfig(envConfig, booking.appointmentType.id).attendeeQuestionIdsGroup;
  return questionIdsGroup && questionIdsGroup.appointmentForSomeoneElseId.split(':');
};

// TODO to be removed. Use selectCustomerAddressFromQuestion below
export const selectUserAddress = (serviceConfig, answers, appointmentTypeId) => {
  const appointmentType = serviceConfig?.appointmentTypes?.[appointmentTypeId] ?? {};
  if (!appointmentType.addressQuestionId) {
    return undefined;
  }

  const address = answers.find((a) => a.questionId === appointmentType.addressQuestionId);

  return address && address.answers[0];
};

export const uiConfig = (state) => state.services.config;

const serviceConfigByServiceName = createSelector(
  servicesSelectors.selectServiceName,
  envSelectors.selectServicesEnvConfig,
  (serviceName, services = {}) => services[serviceName],
);

const appointmentTypeConfigByServiceNameAndAppointmentTypeId = (appointmentTypeId) =>
  createSelector(
    serviceConfigByServiceName,
    ({ appointmentTypes } = {}) => appointmentTypes && appointmentTypes[appointmentTypeId],
  );

const serviceConfig = (appointmentTypeId) =>
  createSelector(
    envSelectors.selectServicesEnvConfig,
    (services) =>
      Object.values(services).find((service) =>
        Object.keys(service.appointmentTypes).find((id) => id === appointmentTypeId),
      ) || {},
  );

export const appointmentTypeConfig = (appointmentTypeId) =>
  createSelector(
    serviceConfig(appointmentTypeId),
    ({ appointmentTypes }) => appointmentTypes && appointmentTypes[appointmentTypeId],
  );

const selectRemoteVersion = (appointmentTypeId) =>
  createSelector(
    appointmentTypeConfig(appointmentTypeId),
    (appointmentType = {}) => appointmentType.remote,
  );

export const selectModeVersion = (appointmentTypeId) =>
  createSelector(
    appointmentTypeConfig(appointmentTypeId),
    (appointmentType = {}) => appointmentType.mode,
  );

export const selectRemoteConfig = (appointmentTypeId) =>
  createSelector(
    uiConfig,
    selectRemoteVersion(appointmentTypeId),
    (config, remoteVersion) => config?.remote?.[remoteVersion],
  );

export const selectModeConfig = (appointmentTypeId) =>
  createSelector(
    uiConfig,
    selectModeVersion(appointmentTypeId),
    (config, modeVersion) => config?.mode?.[modeVersion],
  );

export const selectConfig = (appointmentTypeId) =>
  createSelector(
    uiConfig,
    selectRemoteConfig(appointmentTypeId),
    selectModeConfig(appointmentTypeId),
    (config, remoteConfig, modeConfig) =>
      deepmerge(config, remoteConfig || modeConfig || {}, {
        arrayMerge: (destinationArray, sourceArray) => sourceArray,
      }),
  );

export const selectHeadlineTitle = (appointmentTypeId) =>
  createSelector(
    appointmentTypeConfig(appointmentTypeId),
    (appointmentType = {}) => appointmentType.headlineTitle,
  );

export const selectTabTitle = (appointmentTypeId) =>
  createSelector(
    appointmentTypeConfig(appointmentTypeId),
    envSelectors.selectServicesEnvConfig,
    (appointmentType = {}, service) =>
      appointmentType.tabTitle || service[api.SERVICE_NAME].tabTitle,
  );

export const selectMetaDescription = (appointmentTypeId) =>
  createSelector(
    appointmentTypeConfig(appointmentTypeId),
    envSelectors.selectServicesEnvConfig,
    (appointmentType = {}, service) =>
      appointmentType.metaDescription || service[api.SERVICE_NAME].metaDescription,
  );

const isAppointmentTypeRemote = (appointmentTypeId) =>
  createSelector(appointmentTypeConfig(appointmentTypeId), ({ remote: remoteVersion } = {}) =>
    isNotEmpty(remoteVersion),
  );

const isAppointmentTypeRemoteV1 = (appointmentTypeId) =>
  createSelector(
    appointmentTypeConfig(appointmentTypeId),
    ({ remote: remoteVersion } = {}) => remoteVersion === 'v1',
  );

const isAppointmentTypeRemoteV2 = (appointmentTypeId) =>
  createSelector(
    appointmentTypeConfig(appointmentTypeId),
    ({ remote: remoteVersion } = {}) => remoteVersion === 'v2',
  );

const selectAppointmentTypeGender = (appointmentTypeId) =>
  createSelector(appointmentTypeConfig(appointmentTypeId), ({ gender } = {}) => gender || null);

export const selectGetAppointmentsDetails = createSelector(
  uiConfig,
  (config) => config.getAppointmentsDetails,
);

const isProgressBarItemEnabled = (progressBearItemConfigs, progressBarItem) =>
  progressBearItemConfigs.filter(
    (item) => item.disabled && item.pathname === getSafety(progressBarItem, 'props.href', ''),
  ).length === 0;

const progressBar = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    journeyStateSelectors.selectProgressBarItemConfigs,
    (config, progressBearItemConfigs) => {
      const progressBarItems = config?.PROGRESS_BAR_ITEMS;
      return progressBarItems
        ? progressBarItems.filter((e) => isProgressBarItemEnabled(progressBearItemConfigs, e))
        : [];
    },
  );

const editProgressBar = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    journeyStateSelectors.selectProgressBarItemConfigs,
    (config, progressBearItemConfigs) =>
      (config?.EDIT_PROGRESS_BAR_ITEMS ? config.EDIT_PROGRESS_BAR_ITEMS : []).filter((e) =>
        isProgressBarItemEnabled(progressBearItemConfigs, e),
      ),
  );

const selectFirstEditPage = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    editProgressBar(appointmentTypeId),
    (config, editProgressBarItems) => {
      const firstEditPage =
        config?.editMode?.firstEditPagePath || getSafety(editProgressBarItems[0], 'props.href', '');
      return firstEditPage ? `/${firstEditPage}` : '';
    },
  );

const remoteShopAddress = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(config, remote(version)`shopAddress`),
  );

const reviewFormShopDetails = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(
      config,
      remote(version)`ReviewForm.shopDetails.title`,
      getSafety(config, 'ReviewForm.shopDetails.title'),
    ),
  );

const shopsNextPath = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(config, remote(version)`shopsNextPath`, config.shopsNextPath),
  );

const editShopNextPath = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(config, remote(version)`editShopNextPath`, config.editShopNextPath),
  );

const searchResultDescription = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.searchResultDescription);

const searchResultTitle = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(config, remote(version)`searchResultTitle`),
  );

const remoteAdditionalShopTitle = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(config, remote(version)`additionalShopTitle`),
  );

const shopAddress = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(config, remote(version)`shopAddress`),
  );

const remotePhoneNumberConfig = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(config, remote(version)`AboutYou.YourDetails.phoneNumberConfig`),
  );

const remoteConfig = (appointmentTypeId) =>
  createSelector(
    uiConfig,
    selectRemoteVersion(appointmentTypeId),
    (config, version) => config?.remote?.[version] ?? {},
  );

const selectBookingAppointmentInformation = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(
      config,
      remote(version)`BookingInformation.appointmentInformation`,
      getSafety(config, 'BookingInformation.appointmentInformation'),
    ),
  );

const hideShopDetails = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.ReviewForm?.hideShopDetails);

const hideShopAddress = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.ReviewForm?.hideShopAddress);

const hideEditDateTitle = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.editMode?.hideEditDateTitle);

const hideEditDateGoBackButton = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.editMode?.hideGoBackButton);

const bookingSuccessBookingMessageParagraph = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(
      config,
      remote(version)`SuccessBookingMessage.paragraph`,
      getSafety(config, 'SuccessBookingMessage.paragraph'),
    ),
  );

const selectSuccessBookingMessageAdditionalDescription = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    (config) => config?.SuccessBookingMessage?.additionalDescription,
  );

const selectIcsEvent = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.BookingReview?.icsEvent);

const selectIsServiceNameIncludedToAppointmentTypeName = (appointmentTypeId) =>
  createSelector(
    selectIcsEvent(appointmentTypeId),
    (icsEventConfig = {}) => icsEventConfig.isServiceNameIncludedToAppointmentTypeName,
  );

const selectIsVirtualAppointment = (appointmentTypeId) =>
  createSelector(
    selectIcsEvent(appointmentTypeId),
    (icsEventConfig = {}) => icsEventConfig.isVirtualAppointment,
  );

const bookingReviewShopDetails = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(
      config,
      remote(version)`BookingReview.shopDetails.title`,
      getSafety(config, 'BookingReview.shopDetails.title'),
    ),
  );

const selectHideShopDetailsOnCompletePage = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    (config) => config?.BookingReview?.hideShopDetails,
  );

const hideShopAddressOnCompletePage = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    (config) => config?.BookingReview?.hideShopAddress,
  );

const selectHidePartnerDetailsOnCompletePage = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    (config) => config?.BookingReview?.hidePartnerDetails || false,
  );

const selectHideAddToCalendarButtonOnCompletePage = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(
      config,
      remote(version)`BookingReview.hideAddToCalendarButton`,
      getSafety(config, 'BookingReview.hideAddToCalendarButton', false),
    ),
  );

const selectHidePriceDetailsOnCompletePage = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(
      config,
      remote(version)`BookingReview.hidePriceDetails`,
      getSafety(config, 'BookingReview.hidePriceDetails', false),
    ),
  );

const selectFormatBookingAddressFuncOnCompletePage = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(
      config,
      remote(version)`BookingReview.formatBookingAddressFunc`,
      getSafety(config, 'BookingReview.formatBookingAddressFunc', false),
    ),
  );

const selectDisplayPropertyAddressOnCompletePage = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    (config) => config?.BookingReview?.displayPropertyAddress,
  );

const selectHideManageAppointmentsButtonOnCompletePage = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(
      config,
      remote(version)`BookingReview.hideManageAppointmentsButton`,
      getSafety(config, 'BookingReview.hideManageAppointmentsButton', false),
    ),
  );

const selectHidePhoneSectionOnCompletePage = (appointmentTypeId) =>
  createSelector(uiConfig, selectRemoteVersion(appointmentTypeId), (config, version) =>
    getSafety(
      config,
      remote(version)`BookingReview.hidePhoneSection`,
      getSafety(config, 'BookingReview.hidePhoneSection', false),
    ),
  );

export const selectFeatures = createSelector(
  envSelectors.selectEnvConfig,
  (envConfig) => envConfig.features,
);

export const relatedTreatmentsFeature = createSelector(selectFeatures, (features) =>
  getSafety(features, 'beauty_multiple_bookings', {}),
);

export const subtitleComponentFeature = createSelector(selectFeatures, (features) =>
  getSafety(features, 'display_instore_link', {}),
);

export const digiMerchHeaderFeature = createSelector(selectFeatures, (features) =>
  getSafety(features, 'digi_merch_header', {}),
);

export const remoteWordingFeature = createSelector(selectFeatures, (features) =>
  getSafety(features, 'non_geog', {}),
);

export const selectIsPersonalStylingJourney = createSelector(
  envSelectors.selectServicesEnvConfig,
  appointmentDraftSelectors.selectAppointmentTypeId,
  (servicesConfig, appointmentTypeId) =>
    Object.keys(servicesConfig.personalStyling.appointmentTypes).indexOf(appointmentTypeId) !== -1,
);

export const defaultSubtitleComponent = createSelector(
  uiConfig,
  subtitleComponentFeature,
  (config, feature) => feature.enabled && getSafety(config, 'subtitleComponent'),
);

const subtitleComponent = (appointmentTypeId) =>
  createSelector(
    uiConfig,
    selectRemoteVersion(appointmentTypeId),
    defaultSubtitleComponent,
    (config, version, component) =>
      getSafety(config, remote(version)`subtitleComponent`, component),
  );

export const selectSubtitleComponent = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    defaultSubtitleComponent,
    (config, defaultComponent) => config.subtitleComponent || defaultComponent,
  );

const selectChosenPartner = (appointmentTypeId) =>
  createSelector(
    uiConfig,
    selectRemoteVersion(appointmentTypeId),
    appointmentDraftSelectors.selectAppointmentType,
    (config, version, appointmentType) => {
      const chosenPartner = getSafety(config, remote(version)`hideChosenPartner`, false)
        ? undefined
        : config.chosenPartner;
      return appointmentType.groups ? config.chosenPartnerDropdown : chosenPartner;
    },
  );

const selectGroupsDropdown = createSelector(
  uiConfig,
  appointmentDraftSelectors.selectAppointmentType,
  (config, appointmentType) => appointmentType.groups && config.groupsDropdown,
);

export const selectHowWeContactYouQuestionId = createSelector(
  selectServiceEnvConfigByAppointmentTypeId,
  (serviceEnvConfig) => serviceEnvConfig.howWeContactYouQuestionId || null,
);

export const selectDatePrevPath = (appointmentTypeId) =>
  createSelector(
    uiConfig,
    selectRemoteVersion(appointmentTypeId),
    appointmentDraftSelectors.selectShop,
    (config, version, selectedShop) => {
      let prevPath = getSafety(
        config,
        remote(version)`selectDatePrevPath`,
        config.selectDatePrevPath,
      );
      if (prevPath instanceof Function) {
        prevPath = prevPath(selectedShop.shortName);
      }
      return prevPath;
    },
  );

export const selectTimeSlotSectionTitle = createSelector(
  uiConfig,
  (config) => config.timeSlotSectionTitle,
);

function getLandingPageUrlFromAppointment(services, serviceName, appointmentTypeId) {
  return services[serviceName].appointmentTypes[appointmentTypeId].landingPageUrl;
}

function getLandingPageUrlFromService(services, serviceName) {
  return services[serviceName].landingPageUrl;
}

const selectLandingPage = (serviceName, appointmentTypeId) =>
  createSelector(envSelectors.selectServicesEnvConfig, (services) => {
    if (appointmentTypeId) {
      return (
        getLandingPageUrlFromAppointment(services, serviceName, appointmentTypeId) ||
        getLandingPageUrlFromService(services, serviceName)
      );
    }
    return getLandingPageUrlFromService(services, serviceName);
  });

export const selectIsMeasureService = () =>
  createSelector(uiConfig, (config) => getSafety(config, 'isMeasureService', false));

export const selectAboutYouTimeSlotTitle = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.AboutYou?.TimeSlot?.title);

const selectExcludedRedirectionPaths = createSelector(uiConfig, (config) =>
  getSafety(config, 'excludedPaths'),
);

export const selectPhoneNumberInfoExtra = createSelector(
  uiConfig,
  (config) => config?.PhoneNumberInfo?.extra,
);

const selectProductsFormConfigs = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.ProductsForm);

export const selectChooseYourAppointmentTypeQuestions = createSelector(
  envSelectors.selectEnvConfig,
  (envConfig) => envConfig.chooseYourAppointmentTypeQuestions,
);

export const selectAddressQuestionId = (appointmentTypeId) =>
  createSelector(appointmentTypeConfig(appointmentTypeId), (config) => config.addressQuestionId);

export const selectCustomerAddressFromQuestion = (appointmentTypeId) =>
  createSelector(
    selectAddressQuestionId(appointmentTypeId),
    selectAnswers,
    (addressQuestionId, answers) => answers.find((a) => a.questionId === addressQuestionId)?.answer,
  );

export const selectChooseYourAppointmentTypeQuestion = (appointmentTypeId) =>
  createSelector(
    appointmentTypeConfig(appointmentTypeId),
    (config) => config?.chooseYourAppointmentTypeQuestion,
  );

export const selectChooseYourAppointmentTypeQuestionAnswer = (appointmentTypeId) =>
  createSelector(
    selectChooseYourAppointmentTypeQuestion(appointmentTypeId),
    selectAnswers,
    ({ id } = {}, answers) => answers.find((a) => a.questionId === id)?.answer,
  );

const selectIcsHideLocation = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    (config) => config?.BookingReview?.icsEvent?.hideLocation,
  );

const isInStoreAppointmentType = (appointmentTypeId) =>
  createSelector(
    isAppointmentTypeRemote(appointmentTypeId),
    selectChooseYourAppointmentTypeQuestion(appointmentTypeId),
    selectChooseYourAppointmentTypeQuestionAnswer(appointmentTypeId),
    questionsSelectors.selectRetrievedQuestions,
    selectAddressQuestionId(appointmentTypeId),
    selectIcsHideLocation(appointmentTypeId),
    (
      isRemote,
      chooseYourAppointmentTypeQuestion,
      chooseYourAppointmentTypeQuestionAnswer,
      questions = [],
      addressQuestionId,
      hideLocation,
    ) => {
      switch (true) {
        case !!chooseYourAppointmentTypeQuestion: {
          const chooseYourAppointmentTypeQuestionQuestion = questions.find(
            (q) => q.id === chooseYourAppointmentTypeQuestion.id,
          );
          const selectedOptionIndex = chooseYourAppointmentTypeQuestionQuestion?.options.findIndex(
            (option) => option.name === chooseYourAppointmentTypeQuestionAnswer,
          );

          return selectedOptionIndex === +chooseYourAppointmentTypeQuestion.inStoreOptionIndex;
        }
        case !!addressQuestionId:
          return false;
        case hideLocation:
          return false;
        default:
          return !isRemote;
      }
    },
  );

export const selectDepartmentTitle = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    (config) => config?.BookingReview?.BookingHeader?.departmentTitle,
  );

export const selectIsPartnerSelectionEnabled = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.isPartnerSelectionEnabled);

export const selectPartnerTitle = createSelector(uiConfig, (config) => config?.partnerTitle);

export const selectPostcodeSearchFormConfig = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.PostcodeSearchForm ?? {});

export const selectAddressDropdownBuildDefaultOptionLabel = (appointmentTypeId) =>
  createSelector(
    selectPostcodeSearchFormConfig(appointmentTypeId),
    (postcodeSearchFormConfig) => postcodeSearchFormConfig.AddressDropdown?.buildDefaultOptionLabel,
  );

export const selectAddressDropdownAddressFormLinkLabel = (appointmentTypeId) =>
  createSelector(
    selectPostcodeSearchFormConfig(appointmentTypeId),
    (postcodeSearchFormConfig) => postcodeSearchFormConfig.AddressDropdown?.addressFormLinkLabel,
  );

const selectAboutYouFormConfig = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.AboutYouForm);

const selectReviewFormConfig = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.ReviewForm);

const selectFormatTimeSlot = (appointmentTypeId) =>
  createSelector(
    selectAboutYouFormConfig(appointmentTypeId),
    (aboutYouFormConfig) => aboutYouFormConfig?.formatTimeSlot,
  );

const selectErrorMessageForUnsupportedPostcode = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    (config) => config?.PostcodeSearchForm?.errorMessageForUnsupportedPostcode,
  );

export const selectIsServiceWithoutShops = createSelector(
  uiConfig,
  (config) => config.isServiceWithoutShops,
);

const selectYourDetailsConfig = createSelector(
  uiConfig,
  (config) => config?.AboutYouForm?.YourDetails,
);

const selectMainDetailsConfig = (appointmentTypeId) =>
  createSelector(
    selectReviewFormConfig(appointmentTypeId),
    (reviewFormConfig) => reviewFormConfig?.MainDetails,
  );

const selectIsNewBrandPageFeatureEnabled = createSelector(
  selectFeatures,
  (features) => features.new_brand_page.enabled,
);

const selectSelectDateComponentDecisionerConfig = createSelector(
  uiConfig,
  (config) => config?.SelectDate?.componentDecisioner,
);

const selectEditDateComponent = createSelector(
  uiConfig,
  (config) => config?.editMode?.editDateComponent,
);

const selectExternalPrevPathForSelectDate = (appointmentTypeId) =>
  createSelector(
    selectConfig(appointmentTypeId),
    envSelectors.selectEnvConfig,
    (config, envConfig) =>
      config?.getSelectDateExternalPrevPath &&
      config.getSelectDateExternalPrevPath(envConfig, appointmentTypeId),
  );

const selectTitleForSelectDate = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.selectDateTitle);

const selectAboutIntro = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.AboutIntro);

const selectReviewIntro = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.ReviewIntro);

const selectEditDatePrevPath = (appointmentTypeId) =>
  createSelector(selectConfig(appointmentTypeId), (config) => config?.editDatePrevPath);

export const envConfigSelectors = {
  selectServiceConfig,
  selectUserAddress,
  selectThisAppointmentIsForSomeoneElseQuestionId,
  selectAppointmentTypeConfig,
  selectRemoteV2Config,
  selectRemoteV3Config,
  selectRemoteV2ConfigProperty,
  selectRemoteV3ConfigProperty,
  selectRemoteV1ConfigProperty,
  isAppointmentTypeRemote,
  isAppointmentTypeRemoteV1,
  isAppointmentTypeRemoteV2,
  selectMetaDescription,
  selectHeadlineTitle,
  selectTabTitle,
  selectAppointmentTypeGender,
  selectAppointmentForSomeoneElseQuestionIds,
  selectAttendeeNameQuestionIds,
  selectAttendeePhoneQuestionIds,
  selectGiftAppointmentQuestionIds,
  selectServiceEnvConfigByAppointmentTypeId,
  serviceConfig,
  progressBar,
  editProgressBar,
  selectFirstEditPage,
  remoteShopAddress,
  reviewFormShopDetails,
  shopsNextPath,
  editShopNextPath,
  searchResultDescription,
  searchResultTitle,
  shopAddress,
  remoteAdditionalShopTitle,
  remotePhoneNumberConfig,
  remoteConfig,
  selectBookingAppointmentInformation,
  hideShopDetails,
  hideShopAddress,
  selectHideShopDetailsOnCompletePage,
  hideShopAddressOnCompletePage,
  bookingReviewShopDetails,
  hideEditDateTitle,
  hideEditDateGoBackButton,
  selectHideAddToCalendarButtonOnCompletePage,
  selectHidePriceDetailsOnCompletePage,
  selectFormatBookingAddressFuncOnCompletePage,
  selectDisplayPropertyAddressOnCompletePage,
  selectHideManageAppointmentsButtonOnCompletePage,
  selectHidePhoneSectionOnCompletePage,
  selectHidePartnerDetailsOnCompletePage,
  bookingSuccessBookingMessageParagraph,
  subtitleComponent,
  selectChosenPartner,
  selectGroupsDropdown,
  selectHowWeContactYouQuestionId,
  selectDatePrevPath,
  selectGetAppointmentsDetails,
  relatedTreatmentsFeature,
  selectTimeSlotSectionTitle,
  digiMerchHeaderFeature,
  selectIsMeasureService,
  selectLandingPage,
  uiConfig,
  remoteWordingFeature,
  selectAboutYouTimeSlotTitle,
  selectSuccessBookingMessageAdditionalDescription,
  selectIcsEvent,
  appointmentTypeConfig,
  selectExcludedRedirectionPaths,
  serviceConfigByServiceName,
  appointmentTypeConfigByServiceNameAndAppointmentTypeId,
  selectPhoneNumberInfoExtra,
  selectProductsFormConfigs,
  selectChooseYourAppointmentTypeQuestions,
  selectCustomerAddressFromQuestion,
  selectDepartmentTitle,
  selectIsPartnerSelectionEnabled,
  selectPartnerTitle,
  selectAddressDropdownBuildDefaultOptionLabel,
  selectAddressDropdownAddressFormLinkLabel,
  selectPostcodeSearchFormConfig,
  selectConfig,
  selectSubtitleComponent,
  selectFormatTimeSlot,
  selectErrorMessageForUnsupportedPostcode,
  selectIsServiceWithoutShops,
  selectYourDetailsConfig,
  selectIsNewBrandPageFeatureEnabled,
  selectSelectDateComponentDecisionerConfig,
  selectEditDateComponent,
  selectMainDetailsConfig,
  selectIsServiceNameIncludedToAppointmentTypeName,
  isInStoreAppointmentType,
  selectIsVirtualAppointment,
  selectExternalPrevPathForSelectDate,
  selectTitleForSelectDate,
  selectAboutIntro,
  selectReviewIntro,
  selectEditDatePrevPath,
};
